import { Button, Classes, Icon, InputGroup } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { Entity } from 'shared-libs/models/entity'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FormGroup } from 'browser/components/atomic-elements/atoms/form-group/form-group'
import { FormTable } from 'browser/components/atomic-elements/atoms/form-table/form-table'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'
import { Select } from 'browser/components/atomic-elements/atoms/select'
import 'browser/components/json-elements/atoms/_dictionary.scss'

const KEY_COL_WIDTH = 200
const REMOVE_COL_WIDTH = 30

interface IOption {
  label: string
  value: string
}

/** Plain-string dictionary-key editor: commits the rename on blur (not per keystroke, which would rewrite the whole map), reverts if rejected. */
const KeyInput: React.FC<{
  value: string
  isDisabled?: boolean
  onCommit: (value: string) => boolean
}> = ({ value, isDisabled, onCommit }) => {
  const [text, setText] = React.useState(value)
  React.useEffect(() => setText(value), [value])
  return (
    <InputGroup
      fill
      value={text}
      disabled={isDisabled}
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={() => {
        if (!onCommit(text.trim())) {
          setText(value)
        }
      }}
    />
  )
}

/**
 * @uiComponent
 *
 * Editor for an object map (`{ [key]: value }`) — the map analog of `ui:list`. Keys render as a
 * Select (bounded set from `keyOptions` / the schema's `propertyNames` enum) or a plain-string input;
 * values as text (default mode) or the caller's `items` uiSchema (custom mode, rendered by
 * {@link createDictionaryEntryElement} with `ui:dictionaryKey` for the key). Add / remove / rename
 * rewrite the whole map so numeric-string keys stay strings, not array indices.
 *
 * Example — custom entry with the key beside the value:
 * {
 *   "type": "ui:dictionary",
 *   "value": "core_integrations_sftp_ingestion_config.srcFtpConnectionMap",
 *   "addButtonText": "Add Environment",
 *   "items": {
 *     "type": "ui:row",
 *     "children": [
 *       { "type": "ui:dictionaryKey", "className": "col-xs-4" },
 *       { "type": "ui:input", "value": "entityId", "isHorizontalLayout": false, "className": "col-xs-8" }
 *     ]
 *   }
 * }
 */
export interface IUIDictionaryProps extends IBaseProps {
  value?: Record<string, any>
  onChange: (value: any) => void
  entity: Entity
  frames: FramesManager
  isDisabled?: boolean
  isEditable?: boolean
  errors?: any[]
  /** Explicit key-select options; when omitted, derived from the schema's `propertyNames` enum, else keys are plain strings. */
  keyOptions?: IOption[]
  /** Column header for the key. Rendered (together with `valueLabel`) as the table header in both modes. */
  keyLabel?: string
  /** Column header for the value. Rendered (together with `keyLabel`) as the table header in both modes. */
  valueLabel?: string
  /** Default mode: sub-path within each value to edit as text (e.g. "entityId"); omit for scalar values where `map[key]` is the text. */
  valuePath?: string
  /** Placeholder for the value input (default mode only). */
  valuePlaceholder?: string
  /** Text on the add button (default "Add Item"). */
  addButtonText?: string
}

export class UIDictionary extends React.Component<IUIDictionaryProps> {
  public static defaultProps: Partial<IUIDictionaryProps> = {
    addButtonText: 'Add Item',
  }

  public render() {
    const { addButtonText } = this.props
    const entries = this.getEntries()
    const hasCustomEntry = !_.isNil(this.getItemsUiSchema())
    const keyOptions = this.getKeyOptions()
    const usedKeys = new Set(entries)
    // String keys can always add; a bounded set can add only while unused options remain.
    const canAdd =
      !this.isDisabled() && (_.isNull(keyOptions) || (keyOptions || []).some((o) => !usedKeys.has(o.value)))
    return (
      <div className="c-dictionary">
        {!_.isEmpty(entries) && this.props.keyLabel && this.props.valueLabel && this.renderHeader()}
        {entries.map((key) => this.renderRow(key, hasCustomEntry, keyOptions, usedKeys))}
        <Button
          className={classNames('c-abstractList-addButton', Classes.MINIMAL)}
          icon="add"
          small
          disabled={!canAdd}
          onClick={this.handleAddItem}
        >
          {addButtonText}
        </Button>
      </div>
    )
  }

  /**
   * Column titles for the key/value table (both modes). Aligns with a row's `[key @KEY_COL_WIDTH]
   * [value @flex][remove]` — so a custom `items` layout matches by using the same key-column width.
   */
  private renderHeader() {
    const { keyLabel, valueLabel } = this.props
    return (
      <div
        className="u-flex u-alignItemsCenter c-placeholderColor u-bumperBottom--xs"
        style={{ gap: 8 }}
      >
        <div style={{ width: KEY_COL_WIDTH }}>{keyLabel}</div>
        <div style={{ flex: 1 }}>{valueLabel}</div>
        <div style={{ width: REMOVE_COL_WIDTH }} />
      </div>
    )
  }

  /**
   * A row is `[ entry container ][ remove button ]`. The entry container is either the default
   * string→string layout or the caller's fully-custom `items` UI; the remove button always sits
   * outside it.
   */
  private renderRow(key: string, hasCustomEntry: boolean, keyOptions: IOption[] | null, usedKeys: Set<string>) {
    // Resolve this row's key options once (null for string keys); both entry layouts consume them.
    const rowKeyOptions = _.isNull(keyOptions) ? null : this.keyOptionsFor(key, keyOptions, usedKeys)
    return (
      <div key={key} className="c-dictionaryRow u-bumperBottom--sm">
        <div className="c-dictionaryRow-content">
          {hasCustomEntry
            ? this.renderCustomEntry(key, rowKeyOptions)
            : this.renderDefaultEntry(key, rowKeyOptions)}
        </div>
        {/* Same red, absolutely-positioned remove button as ui:list (c-abstractListItem-removeButton),
            so the entry fills the full width and the ✕ sits outside on the right. */}
        <Button
          className={classNames('c-abstractListItem-removeButton', Classes.MINIMAL)}
          disabled={this.isDisabled()}
          onClick={() => this.handleRemoveItem(key)}
        >
          <Icon icon={IconNames.CROSS} />
        </Button>
      </div>
    )
  }

  private renderDefaultEntry(key: string, keyOptions: IOption[] | null) {
    const { valuePath, valuePlaceholder } = this.props
    const isDisabled = this.isDisabled()
    // An object value with no valuePath can't be text-edited; disable rather than clobber it.
    const isUneditableObject = !valuePath && _.isObject(_.get(this.props.value, [key]))
    return (
      <FormTable className="c-dictionaryEntry">
        <div className="c-dictionaryKey" style={{ width: KEY_COL_WIDTH }}>
          {this.renderKey(key, keyOptions)}
        </div>
        <div className="c-dictionaryValue">
          <InputGroup
            fill
            placeholder={isUneditableObject ? 'Object value — set "valuePath" to edit a field' : valuePlaceholder}
            value={this.getDefaultValueText(key)}
            disabled={isDisabled || isUneditableObject}
            onChange={(e) => this.handleDefaultValueChange(key, e.currentTarget.value)}
          />
        </div>
      </FormTable>
    )
  }

  private getDefaultValueText(key: string): string {
    const { valuePath } = this.props
    const entry = _.get(this.props.value, [key])
    const raw = valuePath ? _.get(entry, valuePath) : entry
    if (_.isObject(raw)) {
      return '' // never '[object Object]'; see the isUneditableObject guard in renderDefaultEntry
    }
    return _.isNil(raw) ? '' : String(raw)
  }

  private renderCustomEntry(key: string, keyOptions: IOption[] | null) {
    return createDictionaryEntryElement({
      frames: this.props.frames,
      entryKey: key,
      entryValue: _.get(this.props.value, [key]),
      isDisabled: this.isDisabled(),
      keyOptions, // this row's options (null for string keys), consumed by ui:dictionaryKey
      onRenameKey: (newKey: string) => this.handleKeyChange(key, newKey),
    })
  }

  /** Default-mode key control: enum Select, or a string input when there is no bounded key set. */
  private renderKey(key: string, keyOptions: IOption[] | null) {
    const isDisabled = this.isDisabled()
    const onRename = (newKey: string) => this.handleKeyChange(key, newKey)
    if (_.isNull(keyOptions)) {
      return <KeyInput value={key} isDisabled={isDisabled} onCommit={onRename} />
    }
    return <Select options={keyOptions} value={key} isDisabled={isDisabled} onChange={onRename} />
  }

  private isDisabled(): boolean {
    const { isDisabled, isEditable } = this.props
    return Boolean(isDisabled) || isEditable === false
  }

  private getEntries(): string[] {
    return _.keys(this.props.value || {})
  }

  private getItemsUiSchema(): any {
    return this.props.frames.getContext('uiSchema').items
  }

  /** This row's options: keys unused by other rows plus this row's own key, appending it if it's outside the enum. */
  private keyOptionsFor(key: string, keyOptions: IOption[] | null, usedKeys: Set<string>): IOption[] {
    // usedKeys includes this row's own key, so `o.value === key` keeps it selectable here.
    const options = (keyOptions || []).filter((o) => !usedKeys.has(o.value) || o.value === key)
    if (key && !options.some((o) => o.value === key)) {
      options.push({ label: key, value: key })
    }
    return options
  }

  /**
   * Bounded set of selectable keys: explicit `keyOptions`, else the schema's `propertyNames` enum,
   * else `null` (plain string keys — editable input, add via placeholder).
   */
  private getKeyOptions(): IOption[] | null {
    const { keyOptions } = this.props
    if (!_.isEmpty(keyOptions)) {
      return keyOptions
    }
    const derived = this.derivePropertyNameOptions()
    return _.isEmpty(derived) ? null : derived
  }

  /** Options from the map's `propertyNames` enum. Label == value (no display formatting). */
  private derivePropertyNameOptions(): IOption[] {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    const dataSchemaPath = frames.getContext('dataSchemaPath')
    if (_.isEmpty(dataSchemaPath) || !renderer) {
      return []
    }
    const propertyNames = renderer.getComponentDataSchema(dataSchemaPath.concat('propertyNames'))
    return _.map(propertyNames?.enum, (v) => ({ label: String(v), value: String(v) }))
  }

  private handleAddItem = () => {
    const keyOptions = this.getKeyOptions()
    const used = new Set(this.getEntries())
    // String keys: seed a generated placeholder to edit. Bounded set: first option not already used.
    const newKey = _.isNull(keyOptions)
      ? this.nextPlaceholderKey(used)
      : keyOptions.find((o) => !used.has(o.value))?.value
    if (!newKey) {
      return
    }
    this.commit({ ...this.props.value, [newKey]: this.defaultEntryValue() })
  }

  /** A `newKey`, `newKey2`, … placeholder not already in `used`, for adding a string key. */
  private nextPlaceholderKey(used: Set<string>): string {
    let candidate = 'newKey'
    let i = 2
    while (used.has(candidate)) {
      candidate = `newKey${i++}`
    }
    return candidate
  }

  private handleRemoveItem(key: string) {
    this.commit(_.omit(this.props.value || {}, [key]))
  }

  private handleDefaultValueChange(key: string, text: string) {
    const { valuePath } = this.props
    const value = this.props.value || {}
    // Whole-map write (numeric-key safe); valuePath sets that sub-field on a cloned entry, forcing objects so numeric sub-keys stay keys.
    const entryValue = valuePath
      ? _.setWith(_.cloneDeep(value[key]) || {}, valuePath, text, Object)
      : text
    this.commit({ ...value, [key]: entryValue })
  }

  /** Rename a key (whole-map rewrite); no-op if empty/unchanged (returns false → KeyInput reverts), overwrites on collision. */
  private handleKeyChange(oldKey: string, newKey: string): boolean {
    if (!newKey || newKey === oldKey) {
      return false
    }
    const next: Record<string, any> = {}
    _.forEach(this.props.value || {}, (v, k) => {
      if (k === newKey) {
        return // drop the overwritten entry; the renamed one takes its slot
      }
      next[k === oldKey ? newKey : k] = v
    })
    this.commit(next)
    return true
  }

  private commit(value: Record<string, any>) {
    this.props.onChange(value)
  }

  /** Default value for a newly-added entry, derived from the value subschema. */
  private defaultEntryValue(): any {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    const dataSchemaPath = frames.getContext('dataSchemaPath')
    if (_.isEmpty(dataSchemaPath) || !renderer) {
      return {}
    }
    const valueSchema = renderer.getComponentDataSchema(dataSchemaPath.concat('additionalProperties'))
    if (valueSchema && _.has(valueSchema, 'default')) {
      return _.cloneDeep(valueSchema.default)
    }
    switch (valueSchema?.type) {
      case 'array':
        return []
      case 'string':
      case 'number':
      case 'integer':
      case 'boolean':
        // '' (not undefined) so an added-but-untouched entry isn't dropped on serialization.
        return ''
      default:
        // object / edge-typed values: start as an empty object so per-entry field edits write into
        // an existing object (never turning a numeric key into an array index).
        return {}
    }
  }
}

/**
 * @uiComponent
 *
 * Editable key control for a `ui:dictionary` custom entry — reads the key and rename handler from
 * frame context, so it works anywhere in the `items` layout. Renders a Select or a plain-string
 * input. An explicit `label` prop lays it out horizontally (label beside the control, matching the
 * `ui:inputField` convention); omit it when the dictionary's column header already names the key.
 */
export const UIDictionaryKey: React.FC<any> = ({ frames, className, style, label }) => {
  const entryKey = frames.getContext('dictionaryEntryKey')
  const options: IOption[] | null = frames.getContext('dictionaryKeyOptions')
  const onRename = frames.getContext('renameDictionaryKey')
  const isDisabled = frames.getContext('dictionaryEntryDisabled')
  const keyLabel = label
  const control = _.isNil(options) ? (
    <KeyInput
      value={entryKey}
      isDisabled={isDisabled}
      onCommit={(newKey) => (onRename ? onRename(newKey) : false)}
    />
  ) : (
    <Select
      options={options}
      value={entryKey}
      isDisabled={isDisabled}
      onChange={(newKey: string) => onRename && onRename(newKey)}
    />
  )
  if (!keyLabel) {
    return (
      <div className={classNames('c-dictionaryKey', className)} style={style}>
        {control}
      </div>
    )
  }
  return (
    <FormGroup className={className} isHorizontalLayout>
      <Label isHorizontalLayout text={keyLabel} />
      <div className="c-formGroup-horizontalContent">{control}</div>
    </FormGroup>
  )
}

/**
 * Renders one custom entry through the renderer (the map analog of `ui:list`'s createListItemElement):
 * resolves the value subschema at `additionalProperties`, binds the value path at the entry key, and
 * injects the key / options / rename handler into frame context for `ui:dictionaryKey`.
 */
export function createDictionaryEntryElement(props) {
  const { frames, entryKey, entryValue, keyOptions, isDisabled, onRenameKey } = props
  const renderer = frames.getContext('renderer')
  const uiSchema = frames.getContext('uiSchema').items
  if (!uiSchema) {
    return null
  }
  const uiSchemaPath = frames.getContext('uiSchemaPath').concat('items')
  let dataSchema = frames.getContext('dataSchema')
  let dataSchemaPath = frames.getContext('dataSchemaPath')
  let valuePath
  if (!_.isEmpty(dataSchemaPath)) {
    dataSchemaPath = frames.getContext('dataSchemaPath').concat('additionalProperties')
    dataSchema = renderer.getComponentDataSchema(dataSchemaPath)
    valuePath = frames.getContext('valuePath').concat(entryKey)
  }
  const context = {
    dataSchema,
    dataSchemaPath,
    uiSchema,
    uiSchemaPath,
    valuePath,
    // Entry extras consumed by `ui:dictionaryKey` (and available to any descendant via frames).
    dictionaryEntryKey: entryKey,
    dictionaryKeyOptions: keyOptions,
    dictionaryEntryDisabled: isDisabled,
    renameDictionaryKey: onRenameKey,
  }
  const state = [entryValue, { key: entryKey, value: entryValue }]
  const newFrames = renderer.createChildFrame(frames, context, state)
  return renderer.createElementFromFrame(newFrames, {})
}
